// @ts-nocheck

import React, { useEffect, useState } from "react";
import DashboardLayout from "../../components/layoutContainers/dashboardLayout";
import SoftBox from "../../components/common/softBox";
import DashboardNavbar from "../../components/navbars/dashboardNavbar";
import SoftTypography from "../../components/common/softTypography";
import SoftButton from "../../components/common/softButton";
import {
  Card,
  Grid,
  IconButton,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../Hooks";
import {
  getExpiryReturnConfigs,
  createExpiryReturnConfig,
  editExpiryReturnConfig,
  deleteExpiryReturnConfig,
} from "../../features/expiryReturnConfig";
import { getSapCompaniesQuery } from "../../api/sapConfigApi";

const normalizeCompany = (company: any) => ({
  companyCode:
    company?.companyCode ||
    company?.CompanyCode ||
    company?.BusinessPartner ||
    "",
  companyName:
    company?.companyName ||
    company?.CompanyName ||
    company?.BusinessPartnerFullName ||
    company?.PartnerName ||
    "",
});

const ExpiryReturnConfig: React.FC = () => {
  const dispatch = useAppDispatch();
  const { configs } = useAppSelector((state: any) => state.expiryReturnConfig);

  const [availableCompanies, setAvailableCompanies] = useState<any[]>([]);
  const [selectedCode, setSelectedCode] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const handleFetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const res = await getSapCompaniesQuery();
      let list: any[] = [];
      if (Array.isArray(res?.data)) {
        list = res.data;
      } else if (Array.isArray(res?.data?.d?.results)) {
        list = res.data.d.results;
      } else if (Array.isArray(res?.d?.results)) {
        list = res.d.results;
      }
      setAvailableCompanies(list.map(normalizeCompany));
      if (list.length === 0) {
        toast.info("No companies returned from SAP.");
      }
    } catch (err) {
      toast.error("Failed to fetch companies from SAP.");
    } finally {
      setLoadingCompanies(false);
    }
  };

  useEffect(() => {
    dispatch(getExpiryReturnConfigs());
    handleFetchCompanies();
  }, [dispatch]);

  const handleAdd = async () => {
    if (!selectedCode) {
      toast.error("Please select a company.");
      return;
    }
    const company = availableCompanies.find(
      (c) => c.companyCode === selectedCode
    );
    const result = await dispatch(
      createExpiryReturnConfig({
        companyCode: selectedCode,
        companyName: company?.companyName || "",
        enabled: true,
      })
    );
    if (createExpiryReturnConfig.fulfilled.match(result)) {
      toast.success("Company added to Expiry Return.");
      setSelectedCode("");
      dispatch(getExpiryReturnConfigs());
    } else {
      toast.error((result.payload as string) || "Failed to add company.");
    }
  };

  const handleToggle = async (row: any) => {
    const result = await dispatch(
      editExpiryReturnConfig({
        id: row._id,
        data: {
          companyCode: row.companyCode,
          companyName: row.companyName,
          enabled: !row.enabled,
        },
      })
    );
    if (editExpiryReturnConfig.fulfilled.match(result)) {
      toast.success("Expiry Return configuration updated.");
      dispatch(getExpiryReturnConfigs());
    } else {
      toast.error((result.payload as string) || "Failed to update.");
    }
  };

  const handleDelete = async (row: any) => {
    if (!window.confirm(`Remove ${row.companyCode} from Expiry Return?`)) {
      return;
    }
    const result = await dispatch(deleteExpiryReturnConfig(row._id));
    if (deleteExpiryReturnConfig.fulfilled.match(result)) {
      toast.success("Company removed from Expiry Return.");
      dispatch(getExpiryReturnConfigs());
    } else {
      toast.error((result.payload as string) || "Failed to delete.");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Card sx={{ p: 3, mb: 3 }}>
          <SoftTypography variant="h6" fontWeight="bold" mb={2}>
            Add Company
          </SoftTypography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Company</InputLabel>
                <Select
                  label="Company"
                  value={selectedCode}
                  onChange={(e) => setSelectedCode(e.target.value)}
                >
                  {availableCompanies.length === 0 && (
                    <MenuItem value="" disabled>
                      No companies loaded — click "Fetch from SAP"
                    </MenuItem>
                  )}
                  {availableCompanies.map((c) => (
                    <MenuItem key={c.companyCode} value={c.companyCode}>
                      {c.companyCode}
                      {c.companyName ? ` - ${c.companyName}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <SoftButton
                variant="gradient"
                color="info"
                onClick={handleFetchCompanies}
                disabled={loadingCompanies}
              >
                {loadingCompanies ? "Fetching..." : "Fetch from SAP"}
              </SoftButton>
            </Grid>
            <Grid item xs={12} md={3}>
              <SoftButton
                variant="gradient"
                color="final"
                onClick={handleAdd}
                fullWidth
              >
                Add
              </SoftButton>
            </Grid>
          </Grid>
        </Card>

        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company Code</TableCell>
                  <TableCell>Company Name</TableCell>
                  <TableCell align="center">Show Expiry Return</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <SoftTypography variant="body2" color="text">
                        No companies configured yet.
                      </SoftTypography>
                    </TableCell>
                  </TableRow>
                )}
                {configs.map((row: any) => (
                  <TableRow key={row._id}>
                    <TableCell>{row.companyCode}</TableCell>
                    <TableCell>{row.companyName || "-"}</TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={row.enabled !== false}
                        onChange={() => handleToggle(row)}
                        color="success"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleDelete(row)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
};

export default ExpiryReturnConfig;
